import { mapApprover } from '@webapp/core/data-access/mappers/mapApprover'
import { mapCurrency } from '@webapp/core/data-access/mappers/mapCurrency'
import { mapDepartment } from '@webapp/core/data-access/mappers/mapDepartment'
import { mapRequester } from '@webapp/core/data-access/mappers/mapRequester'
import { type ApprovableExpenseSummaryConstructor } from '@webapp/modules/expense/entities/ApprovableExpenseSummary'
import { type NextApproverChoiceConstructor } from '@webapp/modules/expense/entities/NextApproverChoice'
import { mapApprovableExpenseItemApiModel } from './mapApprovableExpenseItemApiModel'
import {
  type ApprovableExpenseSummaryApiModel,
  type NextApproverChoiceApiModel,
} from '../api-models/ApprovableExpenseSummaryApiModel'

const mapNextApproverChoice = (
  choice: NextApproverChoiceApiModel
): NextApproverChoiceConstructor => ({
  id: choice.id,
  firstName: choice.firstName,
  lastName: choice.lastName,
  levelText: choice.level_text || null,
  approvalDelegatee: choice.approval_delegatee
    ? {
        id: choice.approval_delegatee.id,
        firstName: choice.approval_delegatee.firstName,
        lastName: choice.approval_delegatee.lastName,
        expectedReturnDate: choice.expected_return_date || null,
      }
    : null,
})

export const mapApprovableExpenseSummaryApiModel = (
  summary: ApprovableExpenseSummaryApiModel
): ApprovableExpenseSummaryConstructor => ({
  id: summary.id,
  uuid: summary.uuid,
  title: summary.title,
  status: summary.status,
  submittedDate: summary.createdDate,
  totalAmount: summary.total_amount,
  reimbursableAmount: summary.reimbursable_amount,
  currency: mapCurrency(summary.currency),
  requester: mapRequester(summary.user),
  // department on the summary is the secondary category; department.branch is the primary category
  secondaryCategory: mapDepartment(summary.department),
  primaryCategory: summary.department.branch
    ? { id: summary.department.branch.id, name: summary.department.branch.name }
    : { id: 0, name: '' },
  nextApprover: summary.next_approver
    ? mapApprover(summary.next_approver)
    : null,
  nextApproverChoices: summary.next_approver_choices
    ? summary.next_approver_choices.map(mapNextApproverChoice)
    : null,
  items: summary.items.map(mapApprovableExpenseItemApiModel),
  logs: summary.logs.map((log) => ({
    id: log.id,
    action: log.action,
    dateTime: log.dateTime,
    user: mapApprover(log.user),
  })),
  delegatedBy: summary.delegated_by || null,
  justCreated: summary.just_created,
  justUpdated: summary.just_updated,
})
